import type { OfflineLedger } from '@scrambl/shared/offline'

import type { LedgerSnapshotStorage } from './ledger-snapshot-storage'

/** Preserve every failed owner in this window before a user-requested reload.
 * This makes no server request and never imports a recovery copy into the queue. */
export async function preserveLedgerForReload({
  ledger,
  storage,
  flush,
  assertCurrent,
}: {
  ledger: Pick<OfflineLedger, 'list'>
  storage: Pick<
    LedgerSnapshotStorage,
    'recoveryOwners' | 'recoveryStatus' | 'preserveForReload'
  >
  flush(): Promise<void>
  assertCurrent(): void
}) {
  const serialize = () => {
    const groups = new Map<string, unknown[]>(
      storage.recoveryOwners().map(owner => [owner, []]),
    )
    for (const entry of ledger.list()) {
      const owner = entry.envelope.ownerId
      const rows = groups.get(owner) ?? []
      rows.push({
        envelope: entry.envelope,
        status: entry.status,
        attempts: entry.attempts,
        updatedAt: entry.updatedAt,
        message: entry.message,
        ...(entry.completionOnly ? { completionOnly: true } : {}),
      })
      groups.set(owner, rows)
    }
    return [...groups].map(
      ([owner, entries]) =>
        [owner, entries.length ? JSON.stringify(entries) : null] as const,
    )
  }
  for (let attempt = 0; attempt < 4; attempt++) {
    assertCurrent()
    await flush().catch(() => undefined)
    assertCurrent()
    const before = serialize()
    for (const [owner, value] of before)
      await storage.preserveForReload(owner, value)
    assertCurrent()
    if (JSON.stringify(before) === JSON.stringify(serialize())) return
  }
  throw new Error(
    'Saved changes are still updating. Keep this window open and try again.',
  )
}
